import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './curve-theme.css';

export type ProjectFilterItem = {
  id: string;
  label: string;
};

type ProjectFilterDropdownProps = {
  items: ProjectFilterItem[];
  selectedId: string;
  onSelect: (id: string) => void;
};

export function ProjectFilterDropdown({
  items,
  selectedId,
  onSelect,
}: ProjectFilterDropdownProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selected = items.find((i) => i.id === selectedId);

  const handleSelect = (id: string) => {
    onSelect(id);
    setOpen(false);
  };

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="curve-btn"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: '6px 10px',
          border: '1px solid var(--curve-border, #E5E5E5)',
          borderRadius: 'var(--curve-radius-md, 8px)',
          background: 'var(--curve-bg-white, #FFFFFF)',
          fontSize: 'var(--curve-text-small, 13px)',
          color: selected
            ? 'var(--curve-text-dark, #1E293B)'
            : 'var(--curve-text-muted, #64748B)',
          fontWeight: selected ? 500 : 400,
          cursor: 'pointer',
          transition: 'border-color var(--curve-transition)',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.borderColor =
            'var(--curve-text-placeholder, #94A3B8)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.borderColor =
            'var(--curve-border, #E5E5E5)';
        }}
      >
        <span>{selected?.label ?? t('ProjectFilter.all_projects')}</span>
        <span
          style={{
            fontSize: '10px',
            color: 'var(--curve-text-placeholder, #94A3B8)',
            transition: 'transform var(--curve-transition)',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          ▼
        </span>
      </button>

      {open && (
        <>
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 100,
            }}
            onClick={() => setOpen(false)}
          />
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 4px)',
              left: 0,
              right: 0,
              background: 'var(--curve-bg-white, #FFFFFF)',
              border: '1px solid var(--curve-border, #E5E5E5)',
              borderRadius: 'var(--curve-radius-md, 8px)',
              boxShadow: 'var(--curve-shadow-md, 0 4px 12px rgba(0,0,0,0.10))',
              zIndex: 101,
              overflow: 'hidden',
              maxHeight: '220px',
              overflowY: 'auto',
            }}
          >
            {/* 全部專案（清除篩選） */}
            <button
              key="__all__"
              onClick={() => handleSelect('')}
              className="curve-btn"
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '8px 12px',
                fontSize: 'var(--curve-text-small, 13px)',
                color:
                  !selectedId
                    ? 'var(--curve-primary, #22C55E)'
                    : 'var(--curve-text-body, #334155)',
                fontWeight: !selectedId ? 600 : 400,
                background: !selectedId
                  ? 'var(--curve-primary-soft, #F0FDF4)'
                  : 'transparent',
              }}
              onMouseEnter={(e) => {
                if (selectedId)
                  (e.currentTarget as HTMLButtonElement).style.background =
                    'var(--curve-bg-gray, #F5F5F5)';
              }}
              onMouseLeave={(e) => {
                if (selectedId)
                  (e.currentTarget as HTMLButtonElement).style.background =
                    'transparent';
              }}
            >
              {t('ProjectFilter.all_projects')}
            </button>

            {items.map((item) => (
              <button
                key={item.id}
                onClick={() => handleSelect(item.id)}
                className="curve-btn"
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 12px',
                  fontSize: 'var(--curve-text-small, 13px)',
                  color:
                    item.id === selectedId
                      ? 'var(--curve-primary, #22C55E)'
                      : 'var(--curve-text-body, #334155)',
                  fontWeight: item.id === selectedId ? 600 : 400,
                  background:
                    item.id === selectedId
                      ? 'var(--curve-primary-soft, #F0FDF4)'
                      : 'transparent',
                }}
                onMouseEnter={(e) => {
                  if (item.id !== selectedId)
                    (e.currentTarget as HTMLButtonElement).style.background =
                      'var(--curve-bg-gray, #F5F5F5)';
                }}
                onMouseLeave={(e) => {
                  if (item.id !== selectedId)
                    (e.currentTarget as HTMLButtonElement).style.background =
                      'transparent';
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
